import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Loader2,
  Users2,
  RefreshCw,
  ImageIcon,
  Search,
  Ban,
  Building2,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  FileDown,
  ChevronsUpDown,
  Plus,
  Pencil,
  Trash2,
} from "lucide-react";
import { MitraEkstraDialog } from "@/components/MitraEkstraDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const EKSTRA_ID = "ekstra";

const STATUS_ORDER = ["ACTIVE", "INACTIVE", "BLACKLIST"];

const STATUS_LABEL = {
  ACTIVE: "Aktif",
  INACTIVE: "Tidak Aktif",
  BLACKLIST: "Blacklist",
};

function statusBadgeClass(status) {
  switch ((status || "").toUpperCase()) {
    case "ACTIVE":
      return "bg-green-600 text-white hover:bg-green-600";
    case "INACTIVE":
      return "bg-yellow-500 text-black hover:bg-yellow-500";
    case "BLACKLIST":
      return "bg-red-600 text-white hover:bg-red-600";
    default:
      return "bg-secondary text-secondary-foreground";
  }
}

export default function Mitra() {
  const [cabangData, setCabangData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [blacklistOnly, setBlacklistOnly] = useState(false);
  const [viewer, setViewer] = useState(null); // { url, title }
  const [collapsed, setCollapsed] = useState({});
  const [exporting, setExporting] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const loadCabang = useCallback((id) => {
    const update = (patch) =>
      setCabangData((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
    update({ loading: true, error: null });
    api
      .get(id === EKSTRA_ID ? "/mitra-ekstra" : `/mitra/${id}`)
      .then((r) => update({ data: r.data.data || [], loading: false }))
      .catch((err) =>
        update({
          data: [],
          loading: false,
          error:
            typeof err.response?.data?.detail === "string" || Array.isArray(err.response?.data?.detail)
              ? formatApiErrorDetail(err.response.data.detail)
              : "Gagal memuat data cabang. Periksa koneksi atau API Key cabang.",
        })
      );
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data: cabangList } = await api.get("/cabang");
      const all = [...cabangList, { id: EKSTRA_ID, nama: "Ekstra" }];
      setCabangData(all.map((c) => ({ id: c.id, nama: c.nama, data: [], error: null, loading: true })));
      setLoading(false);
      all.forEach((c) => loadCabang(c.id));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  }, [loadCabang]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const term = search.trim().toLowerCase();

  const matchRow = (m) => {
    if (blacklistOnly && (m.status || "").toUpperCase() !== "BLACKLIST") return false;
    if (!term) return true;
    const nama = (m.nama_ktp || "").toLowerCase();
    const nik = (m.no_ktp || "").toLowerCase();
    return nama.includes(term) || nik.includes(term);
  };

  const groupByStatus = (rows) => {
    const groups = {};
    rows.forEach((r) => {
      const key = (r.status || "LAINNYA").toUpperCase();
      (groups[key] = groups[key] || []).push(r);
    });
    const ordered = [];
    STATUS_ORDER.forEach((s) => {
      if (groups[s]) ordered.push([s, groups[s]]);
    });
    Object.keys(groups)
      .filter((k) => !STATUS_ORDER.includes(k))
      .forEach((k) => ordered.push([k, groups[k]]));
    return ordered;
  };

  const openImage = (url, title) => {
    if (url) setViewer({ url, title });
  };

  const renderTable = (rows, editable) => (
    <div className="overflow-x-auto">
      <Table className="text-xs [&_td]:px-2 [&_td]:py-1 [&_th]:h-7 [&_th]:px-2">
        <TableHeader>
          <TableRow>
            <TableHead className="w-10 uppercase tracking-[0.1em] text-[10px]">No</TableHead>
            <TableHead className="uppercase tracking-[0.1em] text-xs">Foto</TableHead>
            <TableHead className="uppercase tracking-[0.1em] text-xs">Nama Mitra</TableHead>
            <TableHead className="uppercase tracking-[0.1em] text-xs">Nama KTP</TableHead>
            <TableHead className="uppercase tracking-[0.1em] text-xs">No KTP</TableHead>
            <TableHead className="uppercase tracking-[0.1em] text-xs">Foto KTP</TableHead>
            <TableHead className="uppercase tracking-[0.1em] text-xs">Keterangan</TableHead>
            <TableHead className="uppercase tracking-[0.1em] text-xs">Status</TableHead>
            {editable && <TableHead className="w-20 text-right uppercase tracking-[0.1em] text-xs">Aksi</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((m, i) => (
            <TableRow key={m.id ?? i} data-testid={`mitra-row-${m.id ?? i}`}>
              <TableCell className="text-muted-foreground">{i + 1}</TableCell>
              <TableCell>
                <button
                  type="button"
                  onClick={() => openImage(m.foto_profil, `Foto Profil — ${m.nama_mitra}`)}
                  className={m.foto_profil ? "cursor-pointer" : "cursor-default"}
                  data-testid={`mitra-view-profil-${m.id ?? i}`}
                  title={m.foto_profil ? "Klik untuk melihat foto" : "Belum ada foto"}
                >
                  <Avatar className="h-7 w-7 rounded-sm transition-transform hover:scale-105">
                    {m.foto_profil ? <AvatarImage src={m.foto_profil} alt={m.nama_mitra} /> : null}
                    <AvatarFallback className="rounded-sm bg-secondary text-xs">
                      {(m.nama_mitra || "?").slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </TableCell>
              <TableCell className="font-medium">{m.nama_mitra ?? "-"}</TableCell>
              <TableCell>{m.nama_ktp ?? "-"}</TableCell>
              <TableCell className="font-mono text-xs">{m.no_ktp ?? "-"}</TableCell>
              <TableCell>
                {m.foto_ktp ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 gap-1 px-1.5 text-xs"
                    onClick={() => openImage(m.foto_ktp, `Foto KTP — ${m.nama_mitra}`)}
                    data-testid={`mitra-view-ktp-${m.id ?? i}`}
                  >
                    <ImageIcon className="h-3.5 w-3.5" />
                    Lihat
                  </Button>
                ) : (
                  <span className="text-xs text-muted-foreground">-</span>
                )}
              </TableCell>
              <TableCell className="text-xs" data-testid={`mitra-keterangan-${m.id ?? i}`}>
                <div className="max-w-[260px] whitespace-pre-wrap break-words">{m.keterangan || "-"}</div>
              </TableCell>
              <TableCell>
                <Badge className={`px-1.5 py-0 text-[10px] ${statusBadgeClass(m.status)}`} data-testid={`mitra-status-${m.id ?? i}`}>
                  {m.status ?? "-"}
                </Badge>
              </TableCell>
              {editable && (
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => {
                        setEditing(m);
                        setFormOpen(true);
                      }}
                      data-testid={`mitra-ekstra-edit-${m.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-destructive hover:text-destructive"
                      onClick={() => setDeleting(m)}
                      data-testid={`mitra-ekstra-delete-${m.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  const loadedCount = cabangData.filter((c) => !c.loading).length;
  const anyLoading = loadedCount < cabangData.length;
  const totalShown = cabangData.reduce((acc, c) => acc + c.data.filter(matchRow).length, 0);
  const openCabang = cabangData.filter((c) => !collapsed[c.id]);
  const totalExport = openCabang.reduce((acc, c) => acc + c.data.filter(matchRow).length, 0);
  const allCollapsed = cabangData.length > 0 && openCabang.length === 0;

  const toggleCabang = (id) => setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));
  const toggleAll = () =>
    setCollapsed(allCollapsed ? {} : Object.fromEntries(cabangData.map((c) => [c.id, true])));

  const deleteEkstra = async () => {
    try {
      await api.delete(`/mitra-ekstra/${deleting.id}`);
      toast.success("Mitra dihapus");
      loadCabang(EKSTRA_ID);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDeleting(null);
    }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await api.post(
        "/mitra/export-pdf",
        { cabang_ids: openCabang.map((c) => c.id), search, blacklist_only: blacklistOnly },
        { responseType: "blob", timeout: 180000 }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `mitra_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const failed = JSON.parse(decodeURIComponent(res.headers["x-failed-cabang"] || "%5B%5D"));
      if (failed.length > 0) {
        toast.warning(`PDF dibuat, tetapi cabang berikut gagal dimuat: ${failed.join(", ")}`, {
          duration: 10000,
        });
      } else {
        toast.success(`PDF berhasil dibuat (${totalExport} mitra)`);
      }
    } catch (err) {
      toast.error("Gagal membuat PDF. Silakan coba lagi.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-8" data-testid="mitra-page">
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between fade-up">
        <div>
          <span className="text-sm uppercase tracking-[0.2em] text-muted-foreground">Dashboard</span>
          <h1 className="mt-2 font-heading text-4xl font-bold tracking-tight">Mitra</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Data mitra gabungan dari seluruh cabang, dipisahkan per tabel.
          </p>
        </div>
        <div className="flex gap-2">
        <Button
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
          className="bg-[#1F8FA6] text-white hover:bg-[#177A8F]"
          data-testid="mitra-add-button"
        >
          <Plus className="mr-2 h-4 w-4" />
          Tambah Mitra
        </Button>
        <Button
          variant="outline"
          onClick={loadAll}
          disabled={loading || anyLoading}
          data-testid="mitra-refresh-button"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${loading || anyLoading ? "animate-spin" : ""}`} />
          Muat Ulang
        </Button>
        </div>
      </div>

      {/* Controls */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center fade-up">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Cari Nama KTP atau No KTP..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="mitra-search-input"
          />
        </div>
        <Button
          variant={blacklistOnly ? "default" : "outline"}
          onClick={() => setBlacklistOnly((v) => !v)}
          className={blacklistOnly ? "bg-red-600 hover:bg-red-600/90" : ""}
          data-testid="mitra-blacklist-toggle"
        >
          <Ban className="mr-2 h-4 w-4" />
          {blacklistOnly ? "Tampilkan Semua" : "Daftar Black List"}
        </Button>
        <Button
          variant="outline"
          onClick={toggleAll}
          disabled={cabangData.length === 0}
          data-testid="mitra-toggle-all-button"
        >
          <ChevronsUpDown className="mr-2 h-4 w-4" />
          {allCollapsed ? "Buka Semua" : "Tutup Semua"}
        </Button>
        <Button
          onClick={exportPdf}
          disabled={exporting || loading || anyLoading || totalExport === 0}
          data-testid="mitra-export-pdf-button"
        >
          {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileDown className="mr-2 h-4 w-4" />}
          {exporting ? "Membuat PDF..." : "Export PDF"}
        </Button>
      </div>

      {!loading && anyLoading && (
        <div className="mb-5 fade-up" data-testid="mitra-load-progress">
          <div className="mb-1.5 flex items-center justify-between text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
              Memuat data cabang...
            </span>
            <span data-testid="mitra-load-progress-text">
              {loadedCount} / {cabangData.length} cabang dimuat
            </span>
          </div>
          <Progress value={(loadedCount / cabangData.length) * 100} className="h-1.5" />
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        </div>
      ) : cabangData.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-sm border border-border bg-surface/40 py-24 text-center fade-up">
          <Users2 className="h-6 w-6 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">
            Belum ada cabang. Tambahkan cabang terlebih dahulu di Pengaturan → Cabang.
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          {blacklistOnly && !anyLoading && totalShown === 0 && (
            <div className="rounded-sm border border-border bg-surface/40 py-16 text-center text-sm text-muted-foreground" data-testid="mitra-blacklist-empty">
              Tidak ada mitra berstatus Blacklist.
            </div>
          )}

          {cabangData.map((cab) => {
            const filtered = cab.data.filter(matchRow);
            const grouped = groupByStatus(filtered);
            if (blacklistOnly && !cab.loading && filtered.length === 0) return null;
            return (
              <section key={cab.id} className="fade-up" data-testid={`mitra-cabang-section-${cab.id}`}>
                <button
                  type="button"
                  onClick={() => toggleCabang(cab.id)}
                  className="mb-2 flex w-full items-center gap-2 rounded-sm px-1 py-1 text-left transition-colors hover:bg-surface/60"
                  data-testid={`mitra-cabang-toggle-${cab.id}`}
                  aria-expanded={!collapsed[cab.id]}
                >
                  {collapsed[cab.id] ? <ChevronRight className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                  <Building2 className="h-4 w-4 text-primary" />
                  <h2 className="font-heading text-lg font-semibold tracking-tight">{cab.nama}</h2>
                  {cab.loading ? (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground" data-testid={`mitra-cabang-loading-${cab.id}`}>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" /> memuat...
                    </span>
                  ) : (
                    <span className="text-sm text-muted-foreground">({filtered.length} mitra)</span>
                  )}
                </button>

                {collapsed[cab.id] || cab.loading ? null : cab.error ? (
                  <div className="flex items-center gap-2 rounded-sm border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive" data-testid={`mitra-cabang-error-${cab.id}`}>
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    <span className="flex-1">{cab.error}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 border-destructive/40 text-destructive hover:bg-destructive/10"
                      onClick={() => loadCabang(cab.id)}
                      data-testid={`mitra-cabang-retry-${cab.id}`}
                    >
                      <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                      Coba Ulang
                    </Button>
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="rounded-sm border border-border bg-surface/40 py-10 text-center text-sm text-muted-foreground">
                    Tidak ada data mitra.
                  </div>
                ) : (
                  <div className="space-y-3" data-testid={`mitra-cabang-body-${cab.id}`}>
                    {grouped.map(([status, rows]) => (
                      <div
                        key={status}
                        className="overflow-hidden rounded-sm border border-border bg-surface/40"
                        data-testid={`mitra-status-group-${cab.id}-${status}`}
                      >
                        <div className="flex items-center gap-2 border-b border-border bg-background/60 px-3 py-1.5">
                          <Badge className={statusBadgeClass(status)}>{status}</Badge>
                          <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
                            {STATUS_LABEL[status] || status} · {rows.length}
                          </span>
                        </div>
                        {renderTable(rows, cab.id === EKSTRA_ID)}
                      </div>
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      <MitraEkstraDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        editing={editing}
        onSaved={() => loadCabang(EKSTRA_ID)}
      />

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="mitra-ekstra-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus mitra?</AlertDialogTitle>
            <AlertDialogDescription>
              Mitra "{deleting?.nama_mitra}" beserta fotonya akan dihapus permanen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="mitra-ekstra-delete-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={deleteEkstra}
              data-testid="mitra-ekstra-delete-confirm"
            >
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Image viewer overlay (Foto Profil & Foto KTP) */}
      {viewer && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6"
          onClick={() => setViewer(null)}
          data-testid="mitra-image-overlay"
        >
          <div
            className="relative max-w-lg rounded-sm border border-border bg-background p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 text-sm font-medium">{viewer.title}</div>
            <img
              src={viewer.url}
              alt={viewer.title}
              className="max-h-[60vh] w-full rounded-sm object-contain"
            />
            <div className="mt-4 flex justify-end">
              <Button variant="outline" onClick={() => setViewer(null)} data-testid="mitra-image-close">
                Tutup
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
